import { useCallback } from 'react';
import { useApiData, useAuthenticatedFetch } from '../client';
import type {
  Project,
  ProjectCreateRequest,
  ProjectUpdateRequest,
  Publication,
  ProjectMember,
} from '../../types/media-sim';

export function useProjects(options?: {
  status?: string;
  scope?: string;
  skip?: number;
  limit?: number;
}) {
  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  if (options?.scope) params.set('scope', options.scope);
  if (options?.skip !== undefined) params.set('skip', String(options.skip));
  if (options?.limit !== undefined) params.set('limit', String(options.limit));
  const qs = params.toString();
  return useApiData<Project[]>(qs ? `/media-sim/projects?${qs}` : '/media-sim/projects');
}

export function useProject(projectId: string) {
  return useApiData<Project>(`/media-sim/projects/${projectId}`);
}

export function useCreateProject() {
  const { fetchData } = useAuthenticatedFetch<Project>();
  const createProject = useCallback(
    (data: ProjectCreateRequest) =>
      fetchData('/media-sim/projects', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    [fetchData]
  );
  return { createProject };
}

export function useUpdateProject() {
  const { fetchData } = useAuthenticatedFetch<Project>();
  const updateProject = useCallback(
    (projectId: string, data: ProjectUpdateRequest) =>
      fetchData(`/media-sim/projects/${projectId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    [fetchData]
  );
  return { updateProject };
}

/**
 * Delete a project. The API archives it rather than removing rows, so the
 * project disappears everywhere but its runs and articles are retained and a
 * mistake can be undone in the database. Creator or admin only; returns 409
 * while a run is still executing.
 */
export function useDeleteProject() {
  const { fetchData, loading } = useAuthenticatedFetch<void>();
  const deleteProject = useCallback(
    (projectId: string) =>
      fetchData(`/media-sim/projects/${projectId}`, { method: 'DELETE' }),
    [fetchData]
  );
  return { deleteProject, loading };
}

export function useRegisterProject() {
  const { fetchData } = useAuthenticatedFetch<Project>();
  const registerProject = useCallback(
    (projectId: string) =>
      fetchData(`/media-sim/projects/${projectId}/register`, {
        method: 'POST',
      }),
    [fetchData]
  );
  return { registerProject };
}

export function useProjectPublications(projectId: string) {
  // Guard against an empty id: without this the URL collapses to
  // /projects//publications and the backend 422s trying to parse the segment
  // as a project UUID.
  return useApiData<Publication[]>(
    projectId ? `/media-sim/projects/${projectId}/publications` : ''
  );
}

export function useSetProjectPublications() {
  const { fetchData } = useAuthenticatedFetch<{ publication_ids: string[] }>();
  const setPublications = useCallback(
    (projectId: string, publicationIds: string[]) =>
      fetchData(`/media-sim/projects/${projectId}/publications`, {
        method: 'PUT',
        body: JSON.stringify({ publication_ids: publicationIds }),
      }),
    [fetchData]
  );
  return { setPublications };
}

export function useProjectMembers(projectId: string) {
  // Guard against an empty id (see useProjectPublications).
  return useApiData<ProjectMember[]>(
    projectId ? `/media-sim/projects/${projectId}/members` : ''
  );
}

export function useAddMember() {
  const { fetchData } = useAuthenticatedFetch<ProjectMember>();
  const addMember = useCallback(
    (projectId: string, userId: string) =>
      fetchData(`/media-sim/projects/${projectId}/members`, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId }),
      }),
    [fetchData]
  );
  return { addMember };
}

export function useRemoveMember() {
  const { fetchData } = useAuthenticatedFetch<void>();
  const removeMember = useCallback(
    (projectId: string, memberId: string) =>
      fetchData(`/media-sim/projects/${projectId}/members/${memberId}`, {
        method: 'DELETE',
      }),
    [fetchData]
  );
  return { removeMember };
}
